import { Progress } from 'antd';
import parser from 'html-react-parser';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import deal_background from '../../../../assets/images/deals/deal_bg.svg';
import Offeredby from '../../../../assets/images/deals/offerBy.svg';
import LinkButton from '../../../common/LinkButton';
import { dealStatus, dealTypes } from '../../../login/utils/Status';
import { commaSeperator } from '../../../utils/validation';
import DealTags from '../../dealList/components/deals/DealTags';
import { common_container } from '../../dealList/components/deals/TopCard';
import { ProgressBarPercent } from '../../dealList/utils';

import InterestedModal from '../modal/interested';
import { Deal, Deals } from '../redux/types';
import { createInterestRate, createMinInvestment, isShowFields } from '../utils';

type Props = {
    deal: Deals | undefined;
    termSheetData: string;
};

const titleClass = 'text-[#27AE60] font-[WorkSans] text-xs';
const valueClass = 'text-[#142C53] text-xs font-[WorkSans-Semibold]';
interface InfoBlockProps {
    title: string;
    value?: any;
}
const InfoBlock: React.FC<InfoBlockProps> = ({ title, value = '' }) => (
    <div>
        <p className={titleClass}>{title}</p>
        <p className={valueClass}>{value}</p>
    </div>
);
interface OfferedByProps {
    deal: Deal | undefined;
}
const OfferedBy: React.FC<OfferedByProps> = ({ deal }) => {
    if (!deal) {
        return <></>;
    }
    return (
        <>
            {/** Offered by */}
            <div className="flex items-center mt-2 ">
                <label className="text-[#6D707B] text-xs font-[WorkSans]">Offered by:</label>
                <img src={Offeredby} alt="offeredBy" className="relative top-[1px] ml-1" />
            </div>
            {/**Progress bar */}
            {dealStatus.upcoming !== deal.status && dealTypes.fund !== deal?.info?.summary?.dealType && (
                <>
                    <div className="flex items-center md:w-3/4">
                        <span className="text-[#142C53] font-[WorkSans-SemiBold] text-base mr-2">
                            {isNaN(ProgressBarPercent(deal)) ? 0 : ProgressBarPercent(deal)}%
                        </span>
                        <Progress
                            percent={ProgressBarPercent(deal)}
                            showInfo={false}
                            strokeColor={'#41D17E'}
                            trailColor="#fff"
                            style={{ borderRadius: '0px' }}
                        />
                    </div>
                </>
            )}
        </>
    );
};
const DealDetailCard: React.FC<Props> = ({ deal, termSheetData }) => {
    const navigate = useNavigate();

    const toTermSheet = () => {
        if (deal && deal.deal && deal.deal.uuid) {
            navigate(`/deal/${deal.deal.uuid}/term-sheet`);
        }
    };

    const summary = deal?.deal?.info?.summary;
    const showFields = deal?.deal.status && isShowFields(deal?.deal.status);
    const isImage = deal?.deal?.image || '';
    return (
        <>
            <div className="md:absolute z-10 w-[100%] top-0 relative">
                <div className={`${common_container}`}>
                    {deal && (
                        <div className="bg-[#F1F7FF]  p-8 flex flex-col md:flex-row gap-4 md:pb-2">
                            <div className="w-full md:w-1/2 flex flex-col gap-2 justify-between h-full">
                                <div>
                                    <p className="text-[#6D707B] text-xl font-[WorkSans]">
                                        {deal?.issuer?.category}&nbsp;|&nbsp;{deal?.issuer?.country}
                                    </p>
                                    <p className="text-4xl text-[#020F50] font-[WorkSans-Semibold] mt-2">
                                        {summary?.title}
                                    </p>
                                </div>
                                <p className="text-sm font-[Lato] text-[#020F50] text-justify w-full md:w-[90%]">
                                    {summary?.description}
                                </p>
                                <div className="md:hidden block">
                                    <OfferedBy deal={deal.deal} />
                                </div>
                            </div>
                            <div className="w-full md:w-1/2 flex flex-col gap-4">
                                <div className="">
                                    <DealTags dealsData={deal.deal} limit={undefined} />
                                    {/** Info */}
                                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
                                        {/** Target Funding Size */}
                                        <InfoBlock
                                            title={'Target Funding Size'}
                                            value={
                                                summary?.targetFundingSize
                                                    ? `${summary.targetFundingSize.currency} ${commaSeperator(
                                                          String(summary.targetFundingSize.amountUpto2dp || 0),
                                                      )}`
                                                    : ''
                                            }
                                        />

                                        {/** Asset Class Or Instrument Type */}
                                        <InfoBlock
                                            title={
                                                dealTypes.fund === summary?.dealType ? 'Asset Class' : 'Instrument Type'
                                            }
                                            value={summary?.instrumentType}
                                        />

                                        {/**Fund Manager */}
                                        {dealTypes.fund === summary?.dealType &&
                                            (showFields || dealStatus.upcoming === deal.deal.status) && (
                                                <InfoBlock title={'Fund Manager'} value={deal.issuer?.name} />
                                            )}

                                        {/**Tenor */}
                                        {dealTypes.debt === summary?.dealType &&
                                            (showFields || dealStatus.upcoming === deal.deal.status) && (
                                                <InfoBlock
                                                    title={'Tenor'}
                                                    value={
                                                        summary.terms?.tenorInMonths
                                                            ? `${summary.terms?.tenorInMonths} months`
                                                            : ''
                                                    }
                                                />
                                            )}

                                        {/**Distribution */}
                                        {(dealTypes.debt === summary?.dealType ||
                                            dealTypes.fund === summary?.dealType) &&
                                            showFields && (
                                                <InfoBlock title={'Distribution'} value={summary.terms?.distribution} />
                                            )}

                                        {/**Equity Funding To Date */}
                                        {dealTypes.equity === summary?.dealType && showFields && (
                                            <InfoBlock
                                                title={'Equity Funding To Date'}
                                                value={
                                                    summary.terms?.equityFundingToDate?.amountUpto2dp
                                                        ? `${
                                                              summary.terms.equityFundingToDate.currency
                                                          } ${commaSeperator(
                                                              String(summary.terms.equityFundingToDate.amountUpto2dp),
                                                          )}`
                                                        : ''
                                                }
                                            />
                                        )}

                                        {/**Funding Round */}
                                        {dealTypes.equity === summary?.dealType && showFields && (
                                            <InfoBlock title={'Funding Round'} value={summary.terms?.fundingRound} />
                                        )}

                                        {/** Target Return (p.a.) Or Interest Rate (p.a.)*/}
                                        {(dealTypes.debt === summary?.dealType ||
                                            dealTypes.fund === summary?.dealType) &&
                                            (showFields || deal.deal.status === dealStatus.upcoming) && (
                                                <InfoBlock
                                                    title={
                                                        dealTypes.fund === summary?.dealType
                                                            ? 'Target Return (p.a.)'
                                                            : 'Interest Rate (p.a.)'
                                                    }
                                                    value={parser(createInterestRate(deal.deal))}
                                                />
                                            )}

                                        {/**Min Investment */}
                                        {showFields && createMinInvestment(deal.deal) !== '-' && (
                                            <InfoBlock
                                                title={'Min Investment'}
                                                value={parser(createMinInvestment(deal.deal))}
                                            />
                                        )}

                                        {/**Industry */}
                                        {dealTypes.equity === summary?.dealType && showFields && (
                                            <InfoBlock title={'Industry'} value={deal.issuer?.category} />
                                        )}
                                    </div>
                                    {/** End Info */}
                                </div>

                                <div className="flex flex-wrap items-center gap-2 w-full md:hidden">
                                    <InterestedModal deal={deal.deal} />

                                    {termSheetData && (
                                        <LinkButton title="Term Sheet" isArrow={true} onClick={toTermSheet} />
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {deal && (
                        <div className="bg-[#F1F7FF]  p-8  flex-col md:flex-row items-end gap-4 pt-0 md:flex hidden">
                            <div className="w-full md:w-1/2 flex flex-col gap-1 justify-between h-full">
                                <OfferedBy deal={deal.deal} />
                            </div>

                            <div className="w-full md:w-1/2 flex flex-row gap-4">
                                <div className="flex flex-wrap items-center gap-2 w-full">
                                    <InterestedModal deal={deal.deal} />
                                    {termSheetData && (
                                        <LinkButton title="Term Sheet" isArrow={true} onClick={toTermSheet} />
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div
                    className="w-full z-[-1] bottom-[-1rem] absolute md:hidden block "
                    style={{
                        backgroundImage: `url(${isImage || deal_background})`,
                        minHeight: '309px',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'cover',
                    }}
                ></div>
            </div>
            <div className="relative mt-44 md:block hidden">
                <div
                    style={{
                        backgroundImage: `url(${isImage || deal_background})`,
                        minHeight: '309px',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'cover',
                    }}
                ></div>
            </div>
        </>
    );
};

export default DealDetailCard;
